import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { PageSEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Search, MapPin, Clock, Star, ChevronRight, ArrowRight,
  Store, ShieldCheck, BarChart3, CreditCard, Users, Timer,
  Percent, LayoutDashboard, Wallet, Package
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const tradeCategories = [
  {
    name: 'Restaurant',
    slug: 'Restaurant',
    benefit: 'Boostez vos commandes à emporter',
    image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=600&h=400&fit=crop',
  },
  {
    name: 'Boulangerie',
    slug: 'Boulangerie',
    benefit: 'Fini les queues du matin',
    image: 'https://images.unsplash.com/photo-1509440159596-0249088772ff?w=600&h=400&fit=crop',
  },
  {
    name: 'Boucherie',
    slug: 'Boucherie',
    benefit: 'Vos clients commandent à l\'avance',
    image: 'https://images.unsplash.com/photo-1607623814075-e51df1bdc82f?w=600&h=400&fit=crop',
  },
  {
    name: 'Primeur',
    slug: 'Primeur',
    benefit: 'Paniers de fruits & légumes en un clic',
    image: 'https://images.unsplash.com/photo-1488459716781-31db52582fe9?w=600&h=400&fit=crop',
  },
  {
    name: 'Fromagerie',
    slug: 'Fromagerie',
    benefit: 'Vos plateaux prêts au retrait',
    image: 'https://images.unsplash.com/photo-1486297678162-eb2a19b0a32d?w=600&h=400&fit=crop',
  },
  {
    name: 'Poissonnerie',
    slug: 'Poissonnerie',
    benefit: 'Commandes fraîches du jour',
    image: 'https://images.unsplash.com/photo-1764813824645-e5dc9e49d8cb?w=600&h=400&fit=crop',
  },
  {
    name: 'Traiteur',
    slug: 'Traiteur',
    benefit: 'Pré-commandes et événements',
    image: 'https://images.unsplash.com/photo-1555244162-803834f70033?w=600&h=400&fit=crop',
  },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [featuredRestaurants, setFeaturedRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('client');

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const response = await axios.get(`${API}/restaurants`);
        setFeaturedRestaurants(response.data.slice(0, 4));
      } catch {
        console.error('Error fetching restaurants');
      } finally {
        setLoading(false);
      }
    };
    fetchFeatured();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/restaurants?search=${encodeURIComponent(searchQuery)}`);
  };

  return (
    <Layout>
      <PageSEO.Home />
      {/* ==================== HERO ==================== */}
      <section className="relative bg-gradient-to-br from-emerald-50 via-white to-emerald-50/30 py-14 lg:py-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 tracking-tight leading-[1.1]">
                Vos commerces de quartier,{' '}
                <span className="text-emerald-500">en click & collect</span>
              </h1>
              <p className="mt-5 text-lg text-gray-600 leading-relaxed max-w-xl">
                Commandez en ligne chez vos artisans et commerçants de proximité. Récupérez en quelques minutes, sans attente.
              </p>

              {/* Search */}
              <form onSubmit={handleSearch} className="mt-7 flex gap-2 max-w-lg">
                <div className="relative flex-1">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Rechercher un commerce ou un produit..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-12 h-13 rounded-full border-gray-200 bg-white shadow-sm focus:ring-2 focus:ring-emerald-500/20"
                    data-testid="search-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="h-13 px-7 bg-emerald-500 hover:bg-emerald-600 rounded-full font-semibold"
                  data-testid="search-button"
                >
                  <Search className="w-5 h-5 mr-2" />
                  Rechercher
                </Button>
              </form>

              {/* CTAs */}
              <div className="mt-6 flex flex-wrap gap-3">
                <Button 
                  onClick={() => navigate('/restaurants')}
                  className="bg-gray-900 hover:bg-gray-800 text-white rounded-full px-6 h-11"
                  data-testid="client-cta"
                >
                  Commander maintenant
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/restaurant-signup')}
                  className="rounded-full px-6 border-2 border-emerald-500 text-emerald-600 hover:bg-emerald-50 h-11"
                  data-testid="restaurant-cta"
                >
                  <Store className="w-4 h-4 mr-2" />
                  Je suis commerçant
                </Button>
              </div>

              {/* Merchant mini-block */}
              <div className="mt-7 bg-emerald-50 border border-emerald-200 rounded-2xl p-4 max-w-lg" data-testid="merchant-mini-block">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                    <Store className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 text-sm">
                      Vous êtes commerçant ?
                    </p>
                    <p className="text-sm text-gray-600 mt-0.5">
                      Lancez votre click & collect en 15 minutes, sans engagement.
                    </p>
                    <button
                      onClick={() => navigate('/restaurant-signup')}
                      className="mt-2 text-sm font-semibold text-emerald-600 hover:text-emerald-700 flex items-center gap-1 transition-colors"
                      data-testid="merchant-mini-cta"
                    >
                      Créer ma boutique gratuitement
                      <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative hidden lg:block">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1542838132-92c53300491e?w=800&h=600&fit=crop"
                  alt="Commerce de proximité click and collect"
                  className="w-full h-[480px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
              </div>
              
              {/* Floating Card */}
              <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Prêt en moyenne</p>
                    <p className="font-bold text-gray-900">15-20 min</p>
                  </div>
                </div>
              </div>

              {/* Floating merchants count */}
              <div className="absolute -top-3 -right-3 bg-white rounded-2xl shadow-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-amber-100 flex items-center justify-center">
                    <Users className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Commerçants partenaires</p>
                    <p className="font-bold text-gray-900">Seulement 5% de commission</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== TRADE CATEGORIES ==================== */}
      <section className="py-16 bg-white" data-testid="trade-categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Tous vos commerces de proximité
            </h2>
            <p className="mt-2 text-gray-500 max-w-2xl mx-auto">
              LEMO accompagne tous les métiers de bouche et commerces de quartier dans leur transformation digitale.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {tradeCategories.map((trade) => (
              <Link
                key={trade.name}
                to={`/restaurants?search=${encodeURIComponent(trade.slug)}`}
                className="group relative rounded-2xl overflow-hidden aspect-[4/3]"
                data-testid={`trade-${trade.slug.toLowerCase()}`}
              >
                <img 
                  src={trade.image}
                  alt={trade.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="text-white font-bold text-base sm:text-lg">{trade.name}</h3>
                  <p className="text-white/80 text-xs sm:text-sm mt-0.5 line-clamp-1">{trade.benefit}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== FEATURED SHOPS ==================== */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Commerces à la une
            </h2>
            <Link 
              to="/restaurants"
              className="text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1"
            >
              Voir tout
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl p-4 animate-pulse">
                  <div className="aspect-[4/3] bg-gray-200 rounded-xl mb-4" />
                  <div className="h-4 bg-gray-200 rounded mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-2/3" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredRestaurants.map((restaurant) => (
                <Link 
                  key={restaurant.id}
                  to={`/restaurant/${restaurant.id}`}
                  className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow"
                  data-testid={`restaurant-card-${restaurant.id}`}
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img 
                      src={restaurant.cover_image || 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=400&h=300&fit=crop'}
                      alt={restaurant.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-semibold text-gray-900 group-hover:text-emerald-600 transition-colors">
                        {restaurant.name}
                      </h3>
                      {restaurant.avg_rating > 0 && (
                        <span className="flex items-center gap-1 text-sm font-medium text-gray-700 bg-gray-100 px-2 py-0.5 rounded-full">
                          <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
                          {restaurant.avg_rating.toFixed(1)}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      {restaurant.cuisine_type?.join(', ') || 'Commerce'}
                    </p>
                    <div className="flex items-center gap-3 mt-3 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {restaurant.avg_prep_time} min
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {restaurant.city}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ==================== HOW IT WORKS (TABBED) ==================== */}
      <section className="py-16 bg-white" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-3">
            Comment ça marche ?
          </h2>
          <p className="text-gray-500 text-center mb-8 max-w-xl mx-auto">
            Que vous soyez client ou commerçant, c'est simple et rapide.
          </p>

          {/* Tabs */}
          <div className="flex justify-center gap-2 mb-10">
            <button
              onClick={() => setActiveTab('client')}
              className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all ${
                activeTab === 'client'
                  ? 'bg-emerald-500 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid="tab-client"
            >
              Pour les clients
            </button>
            <button
              onClick={() => setActiveTab('merchant')}
              className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all ${
                activeTab === 'merchant'
                  ? 'bg-emerald-500 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid="tab-merchant"
            >
              Pour les commerçants
            </button>
          </div>

          {/* Client Steps */}
          {activeTab === 'client' && (
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { step: '01', title: 'Choisissez', desc: 'Parcourez vos commerces de quartier et sélectionnez vos produits préférés.' },
                { step: '02', title: 'Commandez', desc: 'Payez en ligne en toute sécurité ou au comptoir lors du retrait.' },
                { step: '03', title: 'Récupérez', desc: 'Passez chercher votre commande sans attendre. C\'est prêt !' },
              ].map((item) => (
                <div key={item.step} className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-emerald-100 text-emerald-600 font-bold text-2xl flex items-center justify-center mx-auto mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              ))}
            </div>
          )}

          {/* Merchant Steps */}
          {activeTab === 'merchant' && (
            <div>
              <div className="grid md:grid-cols-3 gap-8">
                {[
                  { step: '01', title: 'Inscrivez-vous', desc: 'Créez votre boutique en ligne en 15 minutes. Aucune compétence technique requise.' },
                  { step: '02', title: 'Configurez', desc: 'Ajoutez vos produits, définissez vos horaires et vos modes de paiement.' },
                  { step: '03', title: 'Recevez', desc: 'Gérez vos commandes depuis un tableau de bord simple et intuitif.' },
                ].map((item) => (
                  <div key={item.step} className="text-center">
                    <div className="w-16 h-16 rounded-2xl bg-emerald-100 text-emerald-600 font-bold text-2xl flex items-center justify-center mx-auto mb-4">
                      {item.step}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-gray-600">{item.desc}</p>
                  </div>
                ))}
              </div>
              <div className="text-center mt-10">
                <Button
                  onClick={() => navigate('/restaurant-signup')}
                  className="bg-emerald-500 hover:bg-emerald-600 rounded-full px-8 h-12 font-semibold"
                  data-testid="merchant-how-cta"
                >
                  Créer ma boutique
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ==================== MERCHANT BENEFITS ==================== */}
      <section className="py-16 lg:py-20 bg-gray-900 text-white" data-testid="merchant-benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold">
              Votre click & collect prêt en 15 minutes
            </h2>
            <p className="mt-3 text-gray-400 max-w-2xl mx-auto text-base sm:text-lg">
              Rejoignez les commerçants qui simplifient leurs commandes. Sans engagement, sans développeur.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {[
              {
                icon: Percent,
                title: 'Seulement 5% de commission',
                desc: 'L\'une des commissions les plus basses du marché. Pas de frais cachés.',
                color: 'emerald',
              },
              {
                icon: ShieldCheck,
                title: 'Paiement sécurisé Stripe',
                desc: 'Vos clients paient en ligne en toute sécurité. Vous recevez vos fonds rapidement.',
                color: 'blue',
              },
              {
                icon: LayoutDashboard,
                title: 'Tableau de bord simple',
                desc: 'Gérez vos produits, commandes et horaires depuis une interface intuitive.',
                color: 'purple',
              },
              {
                icon: Wallet,
                title: 'Clients paient à l\'avance',
                desc: 'Vos clients règlent avant de venir. Vous préparez sereinement, sans stress.',
                color: 'amber',
              },
            ].map((benefit) => {
              const colorMap = {
                emerald: 'bg-emerald-500/10 text-emerald-400',
                blue: 'bg-blue-500/10 text-blue-400',
                purple: 'bg-purple-500/10 text-purple-400',
                amber: 'bg-amber-500/10 text-amber-400',
              };
              return (
                <div key={benefit.title} className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-colors">
                  <div className={`w-12 h-12 rounded-xl ${colorMap[benefit.color]} flex items-center justify-center mb-4`}>
                    <benefit.icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-semibold text-white mb-2">{benefit.title}</h3>
                  <p className="text-sm text-gray-400 leading-relaxed">{benefit.desc}</p>
                </div>
              );
            })}
          </div>

          <div className="text-center">
            <Button
              onClick={() => navigate('/restaurant-signup')}
              className="bg-white text-gray-900 hover:bg-gray-100 rounded-full px-10 h-13 font-semibold text-base shadow-lg"
              data-testid="merchant-benefits-cta"
            >
              Créer mon click & collect
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <p className="mt-4 text-sm text-gray-500">
              Sans engagement  •  Configuration en 15 minutes  •  Support inclus
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
}
